/* eslint-disable no-console */
import {
  fetchCart,
  getCartLevelDiscountAllocations,
  getCurrencyFromShopifyCart,
  getMRCartFromShopifyCart,
  increaseQuantityAndGetMrCart,
  increaseQuantityAndGetMrCartForVariantsWithQuantity,
} from './cart';

import { serializeFormDataToJSON, serializeFormToJSON } from './html';
import { logger } from './logger';
import {
  getCountryCode,
  getCurrency,
  getCurrencyRate,
  getDevice,
  getPage,
  getShop,
  getShopfloSessionID,
  isArrowInstalled,
  isInaEss,
  isMagicInstalled,
  isSendCartJS,
} from './pageData';

import {
  getIsShopifyFunctionsEnabled,
  getMrDiscountCode,
  getOfferID,
  getOrderID,
  getOrderProperties,
  getStorefrontAccessToken,
  setOfferID,
  setOrderID,
  setWindowKey,
} from './settings';

import { getCustomerId } from './offerData';
import { postData } from './api';
import { getOfferEndpoint } from './constants';
import { mutexManager, StaticMutexKeys } from './mutexmanager';

export function getSelectorForProductForm({ productFormSelector }) {
  let selector = "form[action$='/cart/add']";
  if (getShop() === 'fittify-in.myshopify.com') {
    selector = ".product-form form[action$='/cart/add']";
  }

  if (getShop() === 'swiftoutdoors.myshopify.com') {
    selector = "main form[action$='/cart/add'].shopify-product-form";
  }

  if (
    ['blaqluxury.myshopify.com', 'waiflowcustom.myshopify.com'].includes(
      getShop(),
    )
  ) {
    selector = "form[action$='/cart/add'].product-single__form";
  }

  if (isInaEss()) {
    selector = ".product-form form[action$='/cart/add']";
  }

  if (productFormSelector) {
    selector = productFormSelector;
  }

  return selector;
}

export async function createEmbeddedPageRequest({
  source,
  offerType,
  apiCart,
  widget,
  productFormSelector,
  isCalledFromCartDrawer,
  customVariantsWithQuantity = [],
}) {
  const embeddedPageRequest = { source };

  const isCartPage = getPage() === 'cart';
  const cart = apiCart || (await fetchCart());

  if (cart) {
    const { token } = cart;
    embeddedPageRequest.cart_token = token;
  }
  embeddedPageRequest.cart = getMRCartFromShopifyCart(cart);
  embeddedPageRequest.cart_level_discount_applications = getCartLevelDiscountAllocations(
    cart,
  );
  embeddedPageRequest.currency = getCurrencyFromShopifyCart(cart);
  if (source === 'product_page') {
    embeddedPageRequest.variantsWithQuantity = [];
    if (
      [
        'shoptikidemo.myshopify.com',
        'setu-nutrition.myshopify.com',
        'at-tempo.myshopify.com',
        'd97dc6.myshopify.com',
        'car-touchup-paint.myshopify.com',
      ].includes(getShop())
    ) {
      embeddedPageRequest.variantsWithQuantity = getVariantsWithQuantityForSpecificShop(
        {
          mrCart: embeddedPageRequest.cart,
        },
      );
    } else {
      const selector = getSelectorForProductForm({ productFormSelector });

      let productForms = document.querySelectorAll(selector);

      if (getShop() === 'engrved.myshopify.com' && productForms.length === 2) {
        productForms = [productForms[1]];
      }
      if (
        ['onepricerugs.myshopify.com', 'arearugs-com.myshopify.com'].includes(
          getShop(),
        ) &&
        productForms.length === 2
      ) {
        productForms = [productForms[1]];
      }

      if (productForms) {
        let variantsWithQuantity = [];
        for (let i = 0; i < productForms.length; i += 1) {
          variantsWithQuantity = getVariantsWithQuantityFromForm(
            productForms[i],
          );
          if (variantsWithQuantity.length > 0) {
            break;
          }
        }
        embeddedPageRequest.variantsWithQuantity = variantsWithQuantity;
      }

      // When in cart or home page,  in pdp Fg offer requests, we should send the variantsWithQuantity that was stored when offer became eligible in product page.
      // This is done because backend needs to know the variantsWithQuantity associated with the offer for determining the offer eligibility.
      if (
        offerType === 'freegift' &&
        widget.triggerType === 'product_page' &&
        (isCalledFromCartDrawer || isCartPage)
      ) {
        embeddedPageRequest.variantsWithQuantity = customVariantsWithQuantity;
      }
    }
  }
  embeddedPageRequest.device = getDevice();
  embeddedPageRequest.mr_order_id = getOrderID();
  embeddedPageRequest.shop_url = getShop();
  embeddedPageRequest.page = getPage();
  embeddedPageRequest.offerType = offerType;
  embeddedPageRequest.mrDiscountCode = getMrDiscountCode();
  embeddedPageRequest.customerId = getCustomerId();
  if (widget) {
    embeddedPageRequest.widget = getWidgetRequest({
      widget,
    });
  }

  return embeddedPageRequest;
}

export async function createCheckoutRequest(
  source,
  variants,
  checkOffer,
  discountCode,
) {
  const orderRequest = {};
  let appliedShopifyFunctionCodes = [];
  let shopifyFunctionsCheckoutURL = '';
  let isStorefrontCart = false;
  const cart = await fetchCart();
  if (cart) {
    const { token } = cart;
    orderRequest.cart_token = token;
  }

  if (variants && variants.length > 0) {
    orderRequest.cart = increaseQuantityAndGetMrCart(cart, variants);
  } else {
    orderRequest.cart = getMRCartFromShopifyCart(cart);
  }
  orderRequest.cart_level_discount_applications = getCartLevelDiscountAllocations(
    cart,
  );

  const properties = { attributes: {}, note: '' };

  // set value from cart
  Object.keys(cart.attributes).map(key => {
    properties.attributes[key] = cart.attributes[key];
    return orderRequest;
  });
  properties.note = cart.note;

  // get the dynamic values which maynot have been set and override
  const { attributes, note } = getOrderProperties();
  if (attributes) {
    Object.keys(attributes).map(key => {
      properties.attributes[key] = attributes[key];
      return orderRequest;
    });
  }
  if (note) {
    properties.note = note;
  }

  orderRequest.currency = getCurrencyFromShopifyCart(cart);
  orderRequest.source = source;
  orderRequest.device = getDevice();
  orderRequest.mr_order_id = getOrderID();
  orderRequest.shop_url = getShop();
  orderRequest.page = getPage();
  orderRequest.checkOffer = checkOffer;
  orderRequest.mr_offer_id = getOfferID();
  orderRequest.properties = properties;
  orderRequest.currency_code = getCurrency();
  orderRequest.country_code = getCountryCode();
  orderRequest.conversion_rate = getCurrencyRate();
  orderRequest.pageURL = window.location.href;
  orderRequest.popupTriggerType = 'checkout';
  if (isSendCartJS()) {
    orderRequest.cartJS = cart;
    // orderRequest.isZecpeInstalled = window.isZecpeInstalled;
    orderRequest.isArrowInstalled = isArrowInstalled();
    orderRequest.isMagicInstalled = isMagicInstalled();
  }

  orderRequest.shopflo = {
    sessionID: getShopfloSessionID(),
  };

  if (discountCode) {
    orderRequest.discountCode = discountCode;
  }

  if (getIsShopifyFunctionsEnabled()) {
    const sfModule = await import('../features/shopifyFunctionsDiscount');
    const {
      mrCart,
      appliedCodes,
      cart_level_discount_applications,
      checkoutURL,
      isStorefrontCart: isSFCart,
    } = await sfModule.applyFunctionDiscountOnMrCart({
      mrCart: orderRequest.cart,
      accessToken: getStorefrontAccessToken(),
      countryCode: orderRequest.country_code,
      mrOrderID: getOrderID(),
      shopURL: orderRequest.shop_url,
      cartToken: orderRequest.cart_token,
      cart_level_discount_applications:
        orderRequest.cart_level_discount_applications,
    });
    orderRequest.cart = mrCart;
    orderRequest.cart_level_discount_applications = cart_level_discount_applications;
    appliedShopifyFunctionCodes = appliedCodes;
    shopifyFunctionsCheckoutURL = checkoutURL;
    isStorefrontCart = isSFCart;
  }

  return {
    orderRequest,
    appliedShopifyFunctionCodes: [...new Set(appliedShopifyFunctionCodes)],
    shopifyFunctionsCheckoutURL,
    isStorefrontCart,
  };
}

export async function createPopupOfferRequest(source) {
  const offerRequest = {};

  const cart = await fetchCart();
  if (cart) {
    const { token } = cart;
    offerRequest.cart_token = token;
  }

  offerRequest.cart = getMRCartFromShopifyCart(cart);
  offerRequest.cart_level_discount_applications = getCartLevelDiscountAllocations(
    cart,
  );

  offerRequest.currency = getCurrencyFromShopifyCart(cart);
  offerRequest.source = source;
  offerRequest.device = getDevice();
  offerRequest.mr_order_id = getOrderID();
  offerRequest.mr_offer_id = getOfferID();
  offerRequest.customerId = getCustomerId();

  return offerRequest;
}

const getWidgetRequest = ({ widget }) => {
  const { id, availingType, displayType, offerType, triggerType } = widget;
  return { id, availingType, displayType, offerType, triggerType };
};

function getParentFromTargetOfNodeName({ target, nodeName }) {
  if (target) {
    if (target.nodeName.toLowerCase() === nodeName.toLowerCase()) {
      return target;
    }
    return getParentFromTargetOfNodeName({
      target: target.parentElement,
      nodeName,
    });
  }
  return null;
}

export function getVariantsWithQuantityFromForm(closestForm) {
  try {
    const formdata = new FormData(closestForm);
    const formJSON = {};
    formdata.forEach((value, key) => {
      if (!Reflect.has(formJSON, key)) {
        formJSON[key] = value;
        return [];
      }
      if (!Array.isArray(formJSON[key])) {
        formJSON[key] = [formJSON[key]];
      }
      formJSON[key].push(value);
      return [];
    });

    // id not found case
    if (
      !Reflect.has(formJSON, 'id') &&
      !['herbn-eden.myshopify.com'].includes(getShop())
    ) {
      logger.log('id not found', formJSON);
      // if quantity exists
      if (Reflect.has(formJSON, 'quantity')) {
        const quantity = Number(formJSON.quantity);
        logger.log('quantity', quantity);
        if (quantity && quantity > 0) {
          // get elements with id as name and use them
          const possibleVariants = document.getElementsByName('id');
          for (let i = 0; i < possibleVariants.length; i += 1) {
            const variantID = possibleVariants[i].value;
            if (variantID) {
              return [{ sp_variant_id: Number(variantID), quantity }];
            }
          }
        }
      }
      return [];
    }

    let { id: ids } = formJSON;
    if (!Array.isArray(ids)) {
      ids = [ids];
    }

    let { quantity: quantities } = formJSON;
    if (!quantities) {
      quantities = [];
      for (let i = 0; i < ids.length; i += 1) {
        quantities = quantities.concat(i > 0 ? 0 : 1);
      }
    } else if (!Array.isArray(quantities)) {
      quantities = [quantities];
    }

    const properties = getProductPropertiesFromForm(closestForm);
    const variantsWithQuantity = ids.reduce((items, item, idx) => {
      if (Number(quantities[idx]) > 0) {
        const itemData = {
          sp_variant_id: Number(item),
          quantity: Number(quantities[idx]),
        };
        if (properties) {
          itemData.properties = properties;
        }
        return items.concat(itemData);
      }
      return items;
    }, []);

    return variantsWithQuantity;
  } catch (e) {
    return [];
  }
}

export function getAddtionalOrderProperties() {
  const attributeNodes = document.querySelectorAll('[name^="attributes"]');
  const formdata = new FormData();
  for (let i = 0; i < attributeNodes.length; i += 1) {
    formdata.set(attributeNodes[i].name, attributeNodes[i].value);
  }
  const notes = document.getElementsByName('note');
  if (notes.length > 0) {
    formdata.set(notes[0].name, notes[0].value);
  }

  const serialized = serializeFormDataToJSON(formdata);
  const { attributes, note } = serialized;

  const data = {};
  if (attributes) {
    data.attributes = attributes;
  }
  if (note) {
    data.note = note;
  }

  return data;
}

function getProductPropertiesFromForm(closestForm) {
  logger.log('closest form', !!closestForm);
  if (!closestForm) {
    return {};
  }

  const formData = serializeFormToJSON(closestForm);
  const { properties } = formData;
  if (properties) {
    return properties;
  }
  return {};
}

export function getVariantsWithQuantityForSpecificShop({ target, mrCart }) {
  if (getShop() === 'shoptikidemo.myshopify.com') {
    const mainButton = document.querySelector('.main-ATC');
    if (mainButton) {
      const id = mainButton.getAttribute('id');
      const itemData = { sp_variant_id: Number(id), quantity: 1 };
      return [itemData];
    }

    if (target) {
      const ele = getParentFromTargetOfNodeName({ target, nodeName: 'div' });
      if (ele) {
        const id = ele.getAttribute('id');
        if (id) {
          if (!Number.isNaN(Number(id))) {
            logger.log('data', 'sending');
            return [{ sp_variant_id: Number(id), quantity: 1 }];
          }
        }
      }
    }
  }

  if (mrCart) {
    if (getShop() === 'setu-nutrition.myshopify.com' && mrCart.length > 0) {
      const { sp_variant_id, quantity } = mrCart[0];
      return [{ sp_variant_id, quantity }];
    }
  }

  if (getShop() === 'at-tempo.myshopify.com') {
    const {
      ShopifyAnalytics: {
        meta: {
          product: { variants },
        },
      },
    } = window;

    if (variants) return [{ sp_variant_id: variants[0].id, quantity: 1 }];
  }

  if (getShop() === 'theodinmellow.myshopify.com') {
    const dataElement = document.querySelector(
      '.form-group .tab_options.tab_options_active',
    );
    if (dataElement) {
      const { dataset } = dataElement;
      if (dataset) {
        const { id } = dataset;
        if (id) {
          return [{ sp_variant_id: parseInt(id, 10), quantity: 1 }];
        }
      }
    }
  }

  if (getShop() === 'yoga-studio-store.myshopify.com') {
    const productForm = document.querySelector('#add-to-cart-form');
    if (productForm) {
      return getVariantsWithQuantityFromForm(productForm);
    }
  }

  if (getShop() === 'd97dc6.myshopify.com') {
    const formElement = document.querySelector('.pf-product-form');
    if (formElement) {
      const productID = formElement.getAttribute('data-productid');
      if (productID) {
        const { __pageflyProducts } = window;
        if (__pageflyProducts) {
          const product = __pageflyProducts[productID];
          if (product) {
            const { selected_or_first_available_variant } = product;
            if (selected_or_first_available_variant) {
              const { id } = selected_or_first_available_variant;
              if (id) {
                return [{ sp_variant_id: id, quantity: 1 }];
              }
            }
          }
        }
      }
    }
  }

  if (getShop() === 'car-touchup-paint.myshopify.com') {
    const sp_variant_id = document.querySelector('input[name="id"]');
    if (sp_variant_id && sp_variant_id.value) {
      const quantity = document.getElementById('product-quantity-select');
      if (quantity && quantity.value) {
        logger.log('data', 'sending', sp_variant_id.value, quantity.value);
        return [
          {
            sp_variant_id: Number(sp_variant_id.value),
            quantity: Number(quantity.value),
          },
        ];
      }
    }
  }
  return [];
}

export function getMuseVariantFromEvent(event) {
  const productVariantMap = {
    '6110232936610': '37711743352994',
    '6792721105058': '40016269672610',
    '6153365586082': '37864309817506',
    '6792690729122': '40021572288674',
    '6153308864674': '37864122155170',
    '6745891012770': '39880550711458',
  };
  const { path } = event;
  if (path) {
    // loop on path to find the product id
    for (let i = 0; i < path.length; i += 1) {
      const element = path[i];
      const productID = element.getAttribute('data-product-id');
      if (productID) {
        const variantID = productVariantMap[productID];
        return variantID;
      }
    }
  }
  return 0;
}

export const createAtcPopupOfferRequest = ({
  variantsWithQuantity,
  trigger,
  cart_token,
  cart,
}) => {
  const offerRequestPayload = {
    variantsWithQuantity,
    cart,
    cart_token,
    currency: getCurrencyFromShopifyCart(cart),
    source: trigger === 'atc' ? 'add_to_cart' : 'buy_now',
    device: getDevice(),
    mr_order_id: getOrderID(),
    mr_offer_id: getOfferID(),
    customerId: getCustomerId(),
    popupTriggerType: 'product',
  };

  return offerRequestPayload;
};

const fetchOfferWithTimeout = async ({
  offerRequestPayload,
  timeoutMs = 3000,
}) => {
  try {
    const timeOutPromise = new Promise(resolve => {
      setTimeout(() => {
        console.error('Offer request timed out');
        resolve(false);
      }, timeoutMs);
    });

    // We don't want the customer to wait if the offer request takes too long
    // So we set a timeout for the request
    // Whichever gets resolved first will be returned
    const offerPromise = await Promise.race([
      postData(getOfferEndpoint(), offerRequestPayload),
      timeOutPromise,
    ]);

    return offerPromise;
  } catch (e) {
    console.error('Error fetching offer:', e);
    return false;
  }
};

export async function checkAtcPopupOffersAndStoreData({
  closestForm,
  trigger,
}) {
  // Getting variants with quantity from the form
  const variantsWithQuantity = getVariantsWithQuantityFromForm(closestForm);
  if (variantsWithQuantity.length === 0) {
    return false;
  }
  const cart = await fetchCart();
  const { token } = cart;

  // Adding variants with quantity to the cart because the backend needs it to detemine the eligibility
  const cartContainingVariantsWithQuantity = increaseQuantityAndGetMrCartForVariantsWithQuantity(
    cart,
    variantsWithQuantity,
  );
  const offerRequestPayload = createAtcPopupOfferRequest({
    trigger,
    cart_token: token,
    cart: cartContainingVariantsWithQuantity,
    variantsWithQuantity,
  });

  const pdpPopupMutex = mutexManager.getMutex(StaticMutexKeys.PDP_POPUP);

  const dataToStoreWhenOfferNotEligible = {
    offerResponse: {
      is_eligible: false,
    },
    cartInOfferRequest: [],
  };

  try {
    await pdpPopupMutex.acquire();
    // Making the offer api call
    const offerResponse = await fetchOfferWithTimeout({
      offerRequestPayload,
      timeoutMs: 5000,
    });

    // this means either offer request got timed out or failed
    if (!offerResponse) {
      setWindowKey('mrPdpPopupOfferData', dataToStoreWhenOfferNotEligible);
      return false;
    }

    const {
      is_eligible,
      offer: { triggerType },
      order_id,
    } = offerResponse;

    setOrderID(order_id);

    if (is_eligible && triggerType === 'product') {
      const dataToStore = {
        offerResponse,
        cartInOfferRequest: cartContainingVariantsWithQuantity,
      };
      setWindowKey('mrPdpPopupOfferData', dataToStore);
      setOfferID(offerResponse.offer.mr_offer_id);
    } else {
      setWindowKey('mrPdpPopupOfferData', dataToStoreWhenOfferNotEligible);
    }
  } catch (e) {
    console.error(e);
  } finally {
    pdpPopupMutex.release();
  }

  return false;
}
